
import { useState, useEffect } from "react";
import { Report } from "@/types/report";

interface UseWorksheetFilteringProps {
  reports: Report[];
  totalCount: number;
  itemsPerPage: number;
}

interface UseWorksheetFilteringReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: "ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT";
  setStatusFilter: (status: "ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT") => void;
  showOtherSubjectsOnly: boolean;
  setShowOtherSubjectsOnly: (show: boolean) => void;
  sortField: string;
  sortDirection: "asc" | "desc";
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  handleSort: (field: string) => void;
  normalizeStatus: (status?: string) => "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT";
  resetFilters: () => void;
}

export function useWorksheetFiltering({ reports, totalCount, itemsPerPage }: UseWorksheetFilteringProps): UseWorksheetFilteringReturn {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT">("ALL");
  const [showOtherSubjectsOnly, setShowOtherSubjectsOnly] = useState(false);
  const [sortField, setSortField] = useState("teaching_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);

  const normalizeStatus = (status?: string): "WAITING" | "APPROVED" | "REJECTED" | "NO_PROBLEM_REPORT" => {
    if (!status) return "WAITING";
    switch (status) {
      case "APPROVED":
        return "APPROVED";
      case "REJECTED":
        return "REJECTED";
      case "NO_PROBLEM_REPORT":
        return "NO_PROBLEM_REPORT";
      default:
        return "WAITING";
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setShowOtherSubjectsOnly(false);
    setSortField("teaching_date");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, showOtherSubjectsOnly, sortField, sortDirection]);

  // Ensure current page is valid when total pages change
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    showOtherSubjectsOnly,
    setShowOtherSubjectsOnly,
    sortField,
    sortDirection,
    currentPage,
    setCurrentPage,
    totalPages,
    handleSort,
    normalizeStatus,
    resetFilters,
  };
}
